/**
 * ContributionsPanel — Founder-only browser for RAELA training
 * contributions. Reads via the `founder-list-contributions` edge
 * function (service-role + email-gated). Contributor identity is
 * one-way hashed before leaving the function — the panel only
 * groups by `contributor_hash`, never by raw user_id.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow } from "date-fns";
import { AlertCircle, Database, Download, RefreshCw, Users } from "lucide-react";

const csvEscape = (v: unknown) => {
  const s = v == null ? "" : String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

type Row = {
  id: string;
  app:
    | "fryqnc" | "pulsynq" | "mindlynq"
    | "ablelynq" | "deafwav" | "fullynq" | "jadesynq" | "openlynq" | "spiritsynq";
  prompt: string;
  reply: string;
  surface: string | null;
  was_crisis: boolean;
  rating: number | null;
  trace_id: string | null;
  status: "pending" | "exported" | "purged";
  exported_at: string | null;
  created_at: string;
  contributor_hash: string;
};

type Resp = {
  rows: Row[];
  total: number;
  pendingCount: number;
  activeOptIns: number;
};

const PAGE = 50;

const ContributionsPanel = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [activeOptIns, setActiveOptIns] = useState(0);
  const [app, setApp] = useState<string>("all");
  const [status, setStatus] = useState<string>("pending");
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { limit: PAGE, offset };
      if (app !== "all") body.app = app;
      if (status !== "all") body.status = status;
      const { data, error: err } = await supabase.functions.invoke<Resp>(
        "founder-list-contributions",
        { body },
      );
      if (err) throw err;
      if (!data) throw new Error("empty_response");
      setRows(data.rows);
      setTotal(data.total);
      setPendingCount(data.pendingCount);
      setActiveOptIns(data.activeOptIns);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [app, status, offset]);

  useEffect(() => {
    void fetchPage();
  }, [fetchPage]);

  const uniqueContributors = useMemo(
    () => new Set(rows.map((r) => r.contributor_hash)).size,
    [rows],
  );

  return (
    <div className="space-y-6">
      {/* Header stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-md bg-primary/10 p-2">
              <Users className="h-4 w-4 text-primary" />
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Active opt-ins</div>
              <div className="text-2xl font-semibold">{activeOptIns.toLocaleString()}</div>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-md bg-amber-500/10 p-2">
              <Database className="h-4 w-4 text-amber-500" />
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Pending pairs</div>
              <div className="text-2xl font-semibold">{pendingCount.toLocaleString()}</div>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-md bg-muted p-2">
              <Users className="h-4 w-4" />
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Unique contributors (page)</div>
              <div className="text-2xl font-semibold">{uniqueContributors}</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">App</span>
            <Select value={app} onValueChange={(v) => { setOffset(0); setApp(v); }}>
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="fryqnc">Fryqnc</SelectItem>
                <SelectItem value="pulsynq">Pulsynq</SelectItem>
                <SelectItem value="mindlynq">MindLynq</SelectItem>
                <SelectItem value="ablelynq">Ablelynq</SelectItem>
                <SelectItem value="deafwav">Deafwav</SelectItem>
                <SelectItem value="fullynq">Fullynq</SelectItem>
                <SelectItem value="jadesynq">Jadesynq</SelectItem>
                <SelectItem value="openlynq">Openlynq</SelectItem>
                <SelectItem value="spiritsynq">Spiritsynq</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Status</span>
            <Select value={status} onValueChange={(v) => { setOffset(0); setStatus(v); }}>
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="exported">Exported</SelectItem>
                <SelectItem value="purged">Purged</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const headers = [
                "id","app","surface","was_crisis","rating","status","created_at","contributor_hash","prompt","reply",
              ];
              const lines = [headers.join(",")].concat(
                rows.map((r) =>
                  headers.map((h) => csvEscape((r as Record<string, unknown>)[h])).join(","),
                ),
              );
              const blob = new Blob([lines.join("\n")], { type: "text/csv" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `raela-contributions-${new Date().toISOString().slice(0,10)}.csv`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            disabled={rows.length === 0}
            className="ml-auto"
          >
            <Download className="mr-2 h-3.5 w-3.5" /> Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={fetchPage} disabled={loading}>
            <RefreshCw className={`mr-2 h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </Card>

      {/* Error */}
      {error && (
        <Card className="p-4 border-destructive/40 bg-destructive/5 text-sm flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-destructive mt-0.5" />
          <span>{error}</span>
        </Card>
      )}

      {/* Rows */}
      <Card className="divide-y divide-border">
        {loading && rows.length === 0 && (
          <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
        )}
        {!loading && rows.length === 0 && (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No contributions match these filters yet.
          </div>
        )}
        {rows.map((r) => (
          <div key={r.id} className="p-4 space-y-2">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Badge variant="outline">{r.app}</Badge>
              <Badge
                variant={
                  r.status === "pending" ? "default" : r.status === "exported" ? "secondary" : "outline"
                }
              >
                {r.status}
              </Badge>
              {r.was_crisis && <Badge variant="destructive">crisis</Badge>}
              {r.surface && <span className="text-muted-foreground">{r.surface}</span>}
              <span className="text-muted-foreground">
                {formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}
              </span>
              <span className="ml-auto font-mono text-muted-foreground">
                #{r.contributor_hash}
              </span>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Prompt</div>
              <div className="text-sm whitespace-pre-wrap">{r.prompt}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Reply</div>
              <div className="text-sm whitespace-pre-wrap text-muted-foreground">{r.reply}</div>
            </div>
          </div>
        ))}
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>
          Showing {rows.length === 0 ? 0 : offset + 1}–{offset + rows.length} of {total.toLocaleString()}
        </span>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset(Math.max(0, offset - PAGE))}
            disabled={offset === 0 || loading}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset(offset + PAGE)}
            disabled={offset + rows.length >= total || loading}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ContributionsPanel;
